import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Shield, Lock, Key, Fingerprint, Eye, AlertTriangle, UserCheck, Clock, Globe, Smartphone, Save, Plus, RefreshCw, Filter } from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
const API = process.env.REACT_APP_BACKEND_URL;

const AUDIT_CATEGORIES = [
  { value: 'auth', label: 'Auth', color: 'bg-blue-100 text-blue-700' },
  { value: 'data', label: 'Data', color: 'bg-green-100 text-green-700' },
  { value: 'settings', label: 'Settings', color: 'bg-purple-100 text-purple-700' },
  { value: 'api', label: 'API', color: 'bg-amber-100 text-amber-700' },
  { value: 'general', label: 'General', color: 'bg-gray-100 text-gray-600' },
];
const SEVERITIES = [
  { value: 'info', label: 'Info', color: 'bg-blue-50 text-blue-600' },
  { value: 'warning', label: 'Warning', color: 'bg-amber-50 text-amber-600' },
  { value: 'critical', label: 'Critical', color: 'bg-red-50 text-red-600' },
];

function AuditEntryModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ action: '', category: 'general', severity: 'info', details: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ action: '', category: 'general', severity: 'info', details: '' }); }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.action.trim()) { toast.error('Action is required'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/platform/security/audit-log`, form);
      toast.success('Audit entry created');
      onSaved(data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-amber-500" />New Audit Entry</DialogTitle>
          <DialogDescription>Log a security event</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div><Label>Action *</Label><Input value={form.action} onChange={e => setForm(p => ({ ...p, action: e.target.value }))} placeholder="e.g. Password changed" data-testid="audit-action-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="audit-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{AUDIT_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Severity</Label>
              <Select value={form.severity} onValueChange={v => setForm(p => ({ ...p, severity: v }))}>
                <SelectTrigger data-testid="audit-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITIES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Details</Label><Textarea value={form.details} onChange={e => setForm(p => ({ ...p, details: e.target.value }))} rows={2} placeholder="Additional context..." data-testid="audit-details-input" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-audit-btn">{saving ? 'Saving...' : 'Log Entry'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function timeAgo(d) {
  if (!d) return '';
  const s = Math.floor((new Date() - new Date(d)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function SecuritySettingsPage() {
  const [settings, setSettings] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditModal, setAuditModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [dirty, setDirty] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = filterCategory !== 'all' ? { category: filterCategory } : {};
      const [settingsRes, auditRes] = await Promise.all([
        axios.get(`${API}/api/platform/security/settings`),
        axios.get(`${API}/api/platform/security/audit-log`, { params,  }),
      ]);
      setSettings(settingsRes.data);
      setAuditLog(auditRes.data);
    } catch { toast.error('Failed to load security data'); }
    finally { setLoading(false); }
  }, [filterCategory]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateField = (key, value) => {
    setSettings(p => ({ ...p, [key]: value }));
    setDirty(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { id, ...payload } = settings;
      const { data } = await axios.put(`${API}/api/platform/security/settings`, payload);
      setSettings(data);
      setDirty(false);
      toast.success('Security settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const onAuditSaved = (entry) => { setAuditLog(p => [entry, ...p]); };

  if (loading) return (
    <div className="flex justify-center py-20" data-testid="security-loading">
      <div className="w-8 h-8 rounded-full border-2 border-slate-500 border-t-transparent animate-spin" />
    </div>
  );

  const SETTING_ITEMS = settings ? [
    { icon: Fingerprint, title: 'Two-Factor Authentication', desc: 'Require 2FA on login', type: 'toggle', key: 'two_factor_enabled', value: settings.two_factor_enabled, color: 'text-blue-500 bg-blue-50' },
    { icon: Lock, title: 'Min Password Length', desc: 'Minimum characters required', type: 'number', key: 'password_min_length', value: settings.password_min_length, color: 'text-green-500 bg-green-50' },
    { icon: UserCheck, title: 'Require Uppercase', desc: 'At least one uppercase letter', type: 'toggle', key: 'require_uppercase', value: settings.require_uppercase, color: 'text-purple-500 bg-purple-50' },
    { icon: Key, title: 'Require Numbers', desc: 'At least one numeric character', type: 'toggle', key: 'require_numbers', value: settings.require_numbers, color: 'text-amber-500 bg-amber-50' },
    { icon: Clock, title: 'Session Timeout (min)', desc: 'Auto-logout after inactivity', type: 'number', key: 'session_timeout', value: settings.session_timeout, color: 'text-cyan-500 bg-cyan-50' },
    { icon: AlertTriangle, title: 'Max Login Attempts', desc: 'Before account lockout', type: 'number', key: 'max_login_attempts', value: settings.max_login_attempts, color: 'text-red-500 bg-red-50' },
    { icon: Smartphone, title: 'Lockout Duration (min)', desc: 'Lockout period after max attempts', type: 'number', key: 'lockout_duration', value: settings.lockout_duration, color: 'text-indigo-500 bg-indigo-50' },
    { icon: Globe, title: 'API Key Expiry (days)', desc: 'Auto-expire API keys', type: 'number', key: 'api_key_expiry_days', value: settings.api_key_expiry_days, color: 'text-pink-500 bg-pink-50' },
  ] : [];

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="security-settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center"><Shield className="h-5 w-5 text-slate-600" /></div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Security Settings<PageInfoTooltip pageKey="security-settings" /></h1>
            <p className="text-xs text-gray-400">Account & platform security</p>
          </div>
        </div>
        {dirty && (
          <Button onClick={saveSettings} disabled={saving} className="bg-slate-700 hover:bg-slate-800 gap-1.5 self-start" data-testid="save-security-btn">
            <Save className="h-3.5 w-3.5" />{saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </div>

      {settings && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SETTING_ITEMS.map((s, i) => (
            <Card key={i} className="border-gray-200" data-testid={`security-setting-${s.key}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg ${s.color} flex items-center justify-center flex-shrink-0`}>
                  <s.icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-gray-800">{s.title}</h3>
                  <p className="text-[10px] text-gray-400">{s.desc}</p>
                </div>
                {s.type === 'toggle' ? (
                  <Switch checked={s.value} onCheckedChange={v => updateField(s.key, v)} data-testid={`toggle-${s.key}`} />
                ) : (
                  <Input type="number" className="w-20 h-8 text-sm text-center" value={s.value} onChange={e => updateField(s.key, parseInt(e.target.value) || 0)} data-testid={`input-${s.key}`} />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="border-gray-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-heading font-bold text-gray-700">Audit Log</h2>
            <div className="flex items-center gap-2">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-28 h-8 text-xs" data-testid="audit-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {AUDIT_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => setAuditModal(true)} data-testid="new-audit-btn">
                <Plus className="h-3 w-3" /> Log Entry
              </Button>
            </div>
          </div>

          {auditLog.length === 0 ? (
            <div className="text-center py-8">
              <Eye className="h-10 w-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No audit entries yet</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {auditLog.map((a, i) => {
                const catMeta = AUDIT_CATEGORIES.find(c => c.value === a.category) || AUDIT_CATEGORIES[4];
                const sevMeta = SEVERITIES.find(s => s.value === a.severity) || SEVERITIES[0];
                return (
                  <div key={a.id || i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`audit-entry-${a.id || i}`}>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${a.severity === 'critical' ? 'bg-red-500' : a.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700">{a.action}</p>
                      {a.details && <p className="text-[10px] text-gray-400 truncate">{a.details}</p>}
                      <p className="text-[10px] text-gray-400">{a.user_email || a.user_name || 'System'}</p>
                    </div>
                    <Badge className={`${catMeta.color} text-[9px]`}>{catMeta.label}</Badge>
                    <Badge className={`${sevMeta.color} text-[9px]`}>{sevMeta.label}</Badge>
                    <span className="text-[10px] text-gray-400 flex items-center gap-0.5 w-16 text-right">
                      <Clock className="h-2.5 w-2.5 flex-shrink-0" />{timeAgo(a.created_at)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AuditEntryModal open={auditModal} onClose={() => setAuditModal(false)} onSaved={onAuditSaved} />
    </div>
  );
}
